import React from 'react'
import GatsbyImage from 'gatsby-image'
import { DocumentOutlineSection, H } from 'react-document-section'
import { BLOCKS } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { Trans, t } from '@lingui/macro'

import { i18n } from 'utils/i18n'

import {
  recipe,
  column,
  image,
  title as titleStyle,
  info,
  preparationTimeMin as preparationTimeMinStyle,
  calories as caloriesStyle,
  ingredientsTitle,
  ingredientsList,
  ingredient as ingredientStyle,
  instructionsTitle,
  instructionsList,
  instruction,
} from './index.module.scss'

type Props = Pick<
  Contentful.Recipe,
  'title' | 'preparationTimeMin' | 'calories' | 'ingredients'
> & { image: EF.FluidImageNode; instructions: EF.RTFRawQueryResponse }

const renderInstructionList = (instructions: EF.RTFRawQueryResponse) => {
  return renderRichText(instructions, {
    /* eslint-disable react/display-name */
    renderNode: {
      [BLOCKS.OL_LIST]: (_node, children) => {
        return <ol className={instructionsList}>{children}</ol>
      },
      [BLOCKS.LIST_ITEM]: (_node, children) => {
        return <li className={instruction}>{children}</li>
      },
      [BLOCKS.PARAGRAPH]: (_node, children) => {
        return children
      },
    },
    /* eslint-enable react/display-name */
  })
}

const Recipe = ({
  image: {
    fields: {
      asset: { fluid },
    },
    title: alt,
  },
  title,
  preparationTimeMin,
  calories,
  ingredients,
  instructions,
}: Props) => (
  <DocumentOutlineSection title={title}>
    <article className={recipe}>
      <div className={column}>
        <div className={image}>
          <GatsbyImage
            fluid={{
              ...fluid,
              sizes:
                '(min-width: 532px) 43.6vw, (min-width: 768px) 57vw, (min-width: 820px) 28vw, (min-width: 992px) 43.75vw, (min-width: 1068px) 295px, 80vw',
            }}
            alt={alt}
          />
        </div>
        <H className={titleStyle} />
        <div className={info}>
          {preparationTimeMin && (
            <span className={preparationTimeMinStyle}>
              <Trans>{preparationTimeMin} min</Trans>
            </span>
          )}
          {calories && (
            <span className={caloriesStyle}>
              <Trans>{calories} kcal</Trans>
            </span>
          )}
        </div>
        <DocumentOutlineSection title={i18n._(t`Ingredients`)}>
          <div>
            <H className={ingredientsTitle} />
            <ul className={ingredientsList}>
              {ingredients.map((ingredient) => (
                <li key={ingredient} className={ingredientStyle}>
                  {ingredient}
                </li>
              ))}
            </ul>
          </div>
        </DocumentOutlineSection>
      </div>
      <div className={column}>
        <DocumentOutlineSection title={i18n._(t`Method`)}>
          <div>
            <H className={instructionsTitle} />
            {renderInstructionList(instructions)}
          </div>
        </DocumentOutlineSection>
      </div>
    </article>
  </DocumentOutlineSection>
)

export default Recipe
